import type { JSX } from "react";

import type { FreshnessMeta } from "@/lib/api";

import { ChangeLogTab } from "./tabs/ChangeLogTab";
import { ChecksTab } from "./tabs/ChecksTab";
import { ConfigTab } from "./tabs/ConfigTab";
import { DeferTab } from "./tabs/DeferTab";
import { DraftsTab } from "./tabs/DraftsTab";
import { ExperimentsTab } from "./tabs/ExperimentsTab";
import { HomeTab } from "./tabs/HomeTab";
import { KeywordStatusTab } from "./tabs/KeywordStatusTab";
import { MaintainTab } from "./tabs/MaintainTab";
import { ObjectivesTab } from "./tabs/ObjectivesTab";
import { ReviewTab } from "./tabs/ReviewTab";
import { StatsTab } from "./tabs/StatsTab";
import { TacticsTab } from "./tabs/TacticsTab";

/**
 * The freshness bridge (native-module contract step 2).
 *
 * It returns `undefined`, and that is a deliberate, honest answer rather than a
 * missing implementation. Machine's cockpit API predates the S8 envelope: every
 * one of its section reads (`/state/approvals`, `/state/stats`, `/state/config`,
 * …) returns a bare payload with no `meta`, so there is no date for this module
 * to hand up. The shell renders "we can't confirm this data's date" — which is
 * exactly what is true.
 *
 * The one date that does exist upstream, `intake_freshness`, rides on the FULL
 * `/state` read: the whole two-window decision payload the section endpoints
 * exist to avoid downloading. Pulling it on every tab to fill in one line would
 * cost more than the line is worth, and it describes the INTAKE legs rather than
 * the tab you are looking at, so it would answer a different question than the
 * one the freshness line asks.
 *
 * The fix belongs upstream: `meta` on the section responses (I-6, BR-11). Until
 * then the shell says the honest thing, and never implied-fresh.
 */
export function useMachineMeta(enabled: boolean): FreshnessMeta | null | undefined {
  void enabled;
  return undefined;
}

const TABS: Record<string, () => JSX.Element> = {
  home: HomeTab,
  review: ReviewTab,
  defer: DeferTab,
  maintain: MaintainTab,
  stats: StatsTab,
  stacks: KeywordStatusTab,
  change_log: ChangeLogTab,
  objectives: ObjectivesTab,
  tactics: TacticsTab,
  drafts: DraftsTab,
  experiments: ExperimentsTab,
  checks: ChecksTab,
  config: ConfigTab,
};

/**
 * Machine, rendered natively in the portal.
 *
 * `decision` and `ownerName` are part of the native-module props and are
 * deliberately NOT read here: permission rendering is shell-owned (I-7) and
 * `ShellPage` has already wrapped this in `PermissionGate`. Machine's own
 * permission model is FINER than the portal's — only Damien approves, and Fiona
 * may kill and reorder dominoes — and it is enforced server-side upstream. This
 * module keeps no second copy of those rules; it surfaces the upstream 403 in
 * words at the point of the refused action.
 *
 * An unrecognised `activeTabKey` falls back to Home rather than rendering
 * nothing: the shell owns the tab bar, so a key this module does not know means
 * the manifest and the code have drifted, and a blank viewport hides that while
 * Home shows something true.
 */
export function MachineModule({
  activeTabKey,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  const Tab = (activeTabKey && TABS[activeTabKey]) || HomeTab;
  return <Tab />;
}
